import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditDetailsModal = ({ entry, isOpen, onClose }) => {
  if (!isOpen || !entry) return null;

  const getActionIcon = (action) => {
    switch (action) {
      case 'created': return 'Plus';
      case 'modified': return 'Edit';
      case 'reviewed': return 'CheckCircle';
      case 'exported': return 'Download';
      case 'deleted': return 'Trash2';
      default: return 'Activity';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'created': return 'text-success bg-success/10';
      case 'modified': return 'text-warning bg-warning/10';
      case 'reviewed': return 'text-primary bg-primary/10';
      case 'exported': return 'text-accent bg-accent/10';
      case 'deleted': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const renderChangeDetails = (changes) => {
    if (!changes || changes?.length === 0) return null;

    return (
      <div className="space-y-4">
        {changes?.map((change, index) => (
          <div key={index} className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-foreground">
                {change?.field}
              </h4>
              <span className="text-xs text-muted-foreground">
                Field Change
              </span>
            </div>
            
            <div className="space-y-2">
              {change?.oldValue && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Previous Value:</div>
                  <div className="p-2 bg-error/5 border border-error/20 rounded text-sm text-error line-through">
                    {change?.oldValue}
                  </div>
                </div>
              )}
              
              <div>
                <div className="text-xs text-muted-foreground mb-1">New Value:</div>
                <div className="p-2 bg-success/5 border border-success/20 rounded text-sm text-success">
                  {change?.newValue}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-moderate max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getActionColor(entry?.action)}`}>
              <Icon name={getActionIcon(entry?.action)} size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Audit Entry Details
              </h2>
              <p className="text-sm text-muted-foreground">
                {formatTimestamp(entry?.timestamp)}
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Basic Information */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Basic Information
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Action:</span>
                    <div className="flex items-center space-x-2">
                      <Icon name={getActionIcon(entry?.action)} size={16} className={getActionColor(entry?.action)?.split(' ')?.[0]} />
                      <span className="text-sm font-medium text-foreground capitalize">
                        {entry?.action}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">User:</span>
                    <span className="text-sm text-foreground">{entry?.user}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Invoice Reference:</span>
                    <span className="text-sm font-mono text-foreground">{entry?.invoiceReference}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Description:</span>
                    <span className="text-sm text-foreground">{entry?.description}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Fields Changed:</span>
                    <span className="text-sm text-foreground">{entry?.fieldsChanged?.length}</span>
                  </div>
                </div>
              </div>

              {/* System Information */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  System Information
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Session ID:</span>
                    <span className="text-sm font-mono text-foreground">{entry?.sessionId}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">IP Address:</span>
                    <span className="text-sm font-mono text-foreground">{entry?.ipAddress}</span>
                  </div>
                  
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-muted-foreground">User Agent:</span>
                    <span className="text-xs text-foreground text-right max-w-xs break-all">
                      {entry?.userAgent}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Request ID:</span>
                    <span className="text-sm font-mono text-foreground">{entry?.requestId}</span>
                  </div>
                </div>
              </div>

              {/* Related Information */}
              {entry?.relatedEntries && entry?.relatedEntries?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Related Entries
                  </h3>
                  <div className="space-y-2">
                    {entry?.relatedEntries?.map((related, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                        <div className="flex items-center space-x-2">
                          <Icon name={getActionIcon(related?.action)} size={14} className={getActionColor(related?.action)?.split(' ')?.[0]} />
                          <span className="text-sm text-foreground">{related?.action}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(related.timestamp)?.toLocaleString('en-GB')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Change Details */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Change Details
              </h3>
              {renderChangeDetails(entry?.changes)}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
          
          <Button
            variant="default"
            iconName="Download"
            iconPosition="left"
          >
            Export Entry
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditDetailsModal;